import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { WaterSavedValidationData } from '@metron/shared';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.tsx';

interface WaterSavedValidationChartProps {
  data: WaterSavedValidationData | null;
  month?: string | null; // YYYY-MM format to limit x-axis start
  isLoading?: boolean;
  error?: string | null;
  onClose?: () => void;
}

interface ChartDataPoint {
  day: string;
  displayDay: string;
  totalFlow: number;
  leakFlow: number;
  normalFlow: number;
}

interface TooltipPayload {
  dataKey: string;
  color: string;
  value: number;
  payload: ChartDataPoint;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;

  return (
    <div className='bg-background border border-border rounded-lg shadow-lg p-3'>
      <p className='font-semibold mb-1'>{data.displayDay}</p>
      <p className='text-sm'>
        Total Flow: <span className='font-semibold'>{data.totalFlow.toFixed(2)} GPM</span>
      </p>
      {data.leakFlow > 0 && (
        <p className='text-sm text-red-600'>
          Leak Flow: <span className='font-semibold'>{data.leakFlow.toFixed(2)} GPM</span>
        </p>
      )}
      <p className='text-sm text-blue-600'>
        Normal Flow: <span className='font-semibold'>{data.normalFlow.toFixed(2)} GPM</span>
      </p>
    </div>
  );
}

export function WaterSavedValidationChart({
  data,
  month,
  isLoading,
  error,
  onClose,
}: WaterSavedValidationChartProps) {
  // Calculate month start and end dates for filtering
  const { monthStart, monthEnd } = useMemo(() => {
    if (!month) return { monthStart: null, monthEnd: null };
    const [year, mon] = month.split('-');
    const yearNum = parseInt(year);
    const monNum = parseInt(mon);
    // Get the last day of the month
    const lastDay = new Date(yearNum, monNum, 0).getDate();
    return {
      monthStart: `${year}-${mon}-01`,
      monthEnd: `${year}-${mon}-${String(lastDay).padStart(2, '0')}`,
    };
  }, [month]);

  // Transform data for the chart (stacked bars: normalFlow + leakFlow = totalFlow)
  const chartData = useMemo((): ChartDataPoint[] => {
    if (!data) return [];

    // Create a map of leak days with their rates for quick lookup
    const leakRateMap = new Map(data.leakFlow.map((lf) => [lf.day, lf.rate]));

    return data.dailyFlow
      .filter((point) => {
        // Filter to only include days within the selected month
        if (!monthStart || !monthEnd) return true;
        return point.day >= monthStart && point.day <= monthEnd;
      })
      .map((point) => {
        const date = new Date(point.day);
        const leakFlow = leakRateMap.get(point.day) ?? 0;
        const normalFlow = Math.max(0, point.rate - leakFlow);
        return {
          day: point.day,
          displayDay: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          totalFlow: point.rate,
          leakFlow,
          normalFlow,
        };
      });
  }, [data, monthStart, monthEnd]);

  // Calculate chart bounds
  const { leakStart, leakEnd, leakMean, postLeakMean } = useMemo(() => {
    if (!data) {
      return { leakStart: null, leakEnd: null, leakMean: 0, postLeakMean: 0 };
    }
    return {
      leakStart: data.leakInfo.leakStart,
      leakEnd: data.leakInfo.leakEnd,
      leakMean: data.leakInfo.leakMean,
      postLeakMean: data.leakInfo.postLeakMean,
    };
  }, [data]);

  // Format leak period and post-leak period boundaries for display
  const { leakStartDisplay, leakEndDisplay } = useMemo(() => {
    if (!leakStart || !leakEnd) {
      return { leakStartDisplay: '', leakEndDisplay: '' };
    }
    const startDate = new Date(leakStart);
    const endDate = new Date(leakEnd);
    return {
      leakStartDisplay: startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      leakEndDisplay: endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    };
  }, [leakStart, leakEnd]);

  // Clamp leak period to chart bounds for ReferenceArea
  // IMPORTANT: ReferenceArea x1/x2 values must exactly match displayDay values in chartData
  // otherwise Recharts may render the area incorrectly (e.g., extending to chart edges)
  const { clampedLeakStart, clampedLeakEnd, waterSavedStart } = useMemo(() => {
    if (!leakStart || !leakEnd || chartData.length === 0) {
      return { clampedLeakStart: null, clampedLeakEnd: null, waterSavedStart: null };
    }

    const chartStartDay = chartData[0].day;
    const chartEndDay = chartData[chartData.length - 1].day;

    // Clamp leak start/end to chart bounds (as ISO date strings for comparison)
    const effectiveLeakStart = leakStart < chartStartDay ? chartStartDay : leakStart;
    const effectiveLeakEnd = leakEnd > chartEndDay ? chartEndDay : leakEnd;

    // If leak period is entirely outside chart bounds, return null
    if (effectiveLeakStart > chartEndDay || effectiveLeakEnd < chartStartDay) {
      return { clampedLeakStart: null, clampedLeakEnd: null, waterSavedStart: null };
    }

    // Find actual data points that match the clamped dates
    // This ensures x1/x2 values match exactly with chartData displayDay values
    const leakStartDataPoint = chartData.find((point) => point.day >= effectiveLeakStart);
    const leakEndDataPoint = chartData.findLast((point) => point.day <= effectiveLeakEnd);

    // Water saved area starts the day after leak ends (if within chart bounds)
    const dayAfterLeakEnd = new Date(leakEnd);
    dayAfterLeakEnd.setDate(dayAfterLeakEnd.getDate() + 1);
    const dayAfterLeakEndStr = dayAfterLeakEnd.toISOString().split('T')[0];

    // Find the first data point that's on or after the day after leak ends
    const waterSavedDataPoint = chartData.find((point) => point.day >= dayAfterLeakEndStr);

    return {
      // Use actual displayDay values from chartData for exact ReferenceArea matching
      clampedLeakStart: leakStartDataPoint?.displayDay ?? null,
      clampedLeakEnd: leakEndDataPoint?.displayDay ?? null,
      waterSavedStart: waterSavedDataPoint?.displayDay ?? null,
    };
  }, [leakStart, leakEnd, chartData]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Meter Water Saved</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='flex items-center justify-center h-[400px]'>
            <p className='text-sm text-muted-foreground'>Loading validation data...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Meter Water Saved</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='flex items-center justify-center h-[400px]'>
            <p className='text-sm text-destructive'>Failed to load validation data: {error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <Card>
      <CardHeader className='flex flex-row items-start justify-between'>
        <div>
          <CardTitle className='mb-2'>Meter {data.deviceId} Water Saved</CardTitle>
          <CardDescription>
            Leak period: {leakStartDisplay} - {leakEndDisplay}
            {' • '}
            {data.leakInfo.gallonsSaved.toLocaleString()}{' '}
            gallons saved ({data.leakInfo.pctWaterSaved.toFixed(1)}%)
          </CardDescription>
        </div>
        {onClose && (
          <button
            type='button'
            onClick={onClose}
            className='text-muted-foreground hover:text-foreground'
            aria-label='Close validation chart'
          >
            ✕
          </button>
        )}
      </CardHeader>
      <CardContent className='space-y-2'>
        <div className='[&_svg]:outline-none [&_svg:focus]:outline-none'>
          <ResponsiveContainer width='100%' height={400}>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke='hsl(var(--border))' />
              <XAxis
                dataKey='displayDay'
                tick={{ fontSize: 10 }}
                tickMargin={20}
                interval='preserveStartEnd'
                angle={-45}
                textAnchor='end'
                height={55}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                width={50}
                label={{
                  value: 'GPM',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 11 },
                }}
              />
              <Tooltip content={<CustomTooltip />} />

              {/* Leak period shaded area (red) - clamped to chart bounds */}
              {clampedLeakStart && clampedLeakEnd && (
                <ReferenceArea
                  x1={clampedLeakStart}
                  x2={clampedLeakEnd}
                  fill='#ef4444'
                  fillOpacity={0.1}
                  stroke='#ef4444'
                  strokeOpacity={0.3}
                />
              )}

              {/* Water saved area (green) - from day after leak end to end of chart, between leak mean and post-leak mean */}
              {waterSavedStart && chartData.length > 0 && leakMean > postLeakMean && (
                <ReferenceArea
                  x1={waterSavedStart}
                  x2={chartData[chartData.length - 1].displayDay}
                  y1={postLeakMean}
                  y2={leakMean}
                  fill='#22c55e'
                  fillOpacity={0.15}
                  stroke='#22c55e'
                  strokeOpacity={0.3}
                />
              )}

              {/* Avg water use during leak period (red dashed) — labeled in the legend below */}
              <ReferenceLine
                y={leakMean}
                stroke='#ef4444'
                strokeDasharray='5 5'
                strokeWidth={2}
              />

              {/* Post-leak avg reference line (green dashed) — labeled in the legend below */}
              <ReferenceLine
                y={postLeakMean}
                stroke='#22c55e'
                strokeDasharray='5 5'
                strokeWidth={2}
              />

              {/* Stacked bars: Leak flow (bottom) + Normal flow (top) */}
              <Bar
                dataKey='leakFlow'
                stackId='flow'
                fill='#ef4444'
                name='Leak Flow'
              />
              <Bar
                dataKey='normalFlow'
                stackId='flow'
                fill='hsl(var(--primary))'
                name='Normal Flow'
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Legend — the dashed-line entries direct-label what used to be in-plot text. */}
        <div className='flex items-center justify-center gap-6 text-sm flex-wrap'>
          <div className='flex items-center gap-2'>
            <div className='w-4 h-3 bg-primary rounded-sm' />
            <span>Normal Flow</span>
          </div>
          <div className='flex items-center gap-2'>
            <div className='w-4 h-3 bg-red-500 rounded-sm' />
            <span>Leak Flow</span>
          </div>
          <div className='flex items-center gap-2'>
            <div className='w-4 h-0.5' style={{ borderTop: '2px dashed #ef4444' }} />
            <span>
              Avg use during leak:{' '}
              <span className='font-medium tabular-nums'>{leakMean.toFixed(2)} GPM</span>
            </span>
          </div>
          <div className='flex items-center gap-2'>
            <div className='w-4 h-0.5' style={{ borderTop: '2px dashed #22c55e' }} />
            <span>
              Avg use after leak:{' '}
              <span className='font-medium tabular-nums'>{postLeakMean.toFixed(2)} GPM</span>
            </span>
          </div>
          <div className='flex items-center gap-2'>
            <div className='w-4 h-3 bg-green-500 opacity-30 rounded-sm' />
            <span>Water Saved</span>
          </div>
        </div>

        <p className='text-xs italic text-muted-foreground text-center mt-1'>
          Daily water usage with leak flow highlighted in red. The shaded red area marks the leak
          period; the green area shows water saved after the leak was fixed. Dashed lines indicate
          average usage during and after the leak.
        </p>

        {/* WaterScope Plus Link */}
        <div className='flex justify-center mt-4 pt-4 border-t'>
          <a
            href={`https://plus.waterscope.us/portfolio/${data.customerId}/${data.allSensorsGroupId}/${data.allSensorsGroupId}/${data.sensorId}`}
            target='_blank'
            rel='noopener noreferrer'
            className='inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
          >
            View in WaterScope Plus
          </a>
        </div>
      </CardContent>
    </Card>
  );
}
